import { array, boolean, mixed, object, string } from "yup";
import { useContext, useLayoutEffect } from "react";
import { UserContext } from "@src/utility/context/user";
import { parseUrl } from "@helpers/utils";
import { useForm } from "react-hook-form";
import useSearchParams from "@hooks/use-search-params";
import { yupResolver } from "@hookform/resolvers/yup";
import useAxios from "@hooks/use-axios";
import { useParams } from "react-router-dom";

export const defaultValues = {
    company_name   : "",
    csm            : "",
    sdr            : "",
    product        : "",
    stage          : "",
    max_mrr        : "",
    min_mrr        : "",
    history_enabled: false,
    kpi            : [] 
};

const useFilter = ({ setQueryParams, kpi, getKpi }) => {
    const { appendUrl, getSearchParams } = useSearchParams();
    const { platformId } = useParams();
    const { axiosInstance } = useAxios({ base: "panel", showAlert: false });

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(object({
            history_enabled: boolean(),
            company_name   : string(),
            csm            : mixed().nullable(),
            sdr            : mixed().nullable(),
            product        : mixed().nullable(),
            stage          : mixed().nullable(),
            max_mrr        : string().nullable(),
            min_mrr        : string().nullable(),
            kpi            : array().of(string()).nullable()
        }))
    });

    const { handleSubmit, reset } = methods;

    const {
        userData: { user }
    } = useContext(UserContext);

    const updateKpis = async (selectedKPIData) => {
        const updatedKpis = kpi.map(kpi => ({
            name : kpi.name,
            value: selectedKPIData.includes(kpi.name)
        }));
        await axiosInstance.patch(`/kpi-setting/${platformId}/update`, updatedKpis);
        await getKpi();

    };
    const onSubmit = (onload) => {
        handleSubmit((data) => {
            updateKpis(data.kpi);
            const params = {};
            ["page", "limit", "start", "end", "tab"].forEach((item) => {
                if (!onload) {
                    params["page"] = 1;
                }
                params[item] = getSearchParams(item);
            });
    
            const queryParams = {
                company_name: data.company_name,
                ...(data.stage ? { stage: data.stage.value } : {}),
                max_mrr     : data.max_mrr,
                min_mrr     : data.min_mrr,
                ...params,
                ...(data.csm ? { csm: data.csm.value } : {}),
                ...(data.sdr ? { sdr: data.sdr.value } : {}),
                ...(data.product ? { platform: data.product.value } : {}) 
            };
            appendUrl(parseUrl({ ...queryParams, ...params }));
            setQueryParams({ ...queryParams, ...params });
        })();
    };

    const onReset = () => {
        let userRole = "";
        if (["sdr", "admin_sdr"].includes(user.role)) userRole = "sdr";
        else if (["csm", "admin_csm"].includes(user.role)) userRole = "csm";

        const resetValues = {
            ...defaultValues,
            csm: userRole === "csm" ? { label: user.name, value: user._id } : "",
            sdr: userRole === "sdr" ? { label: user.name, value: user._id } : "",
            kpi: []
        };

        reset(resetValues);
    };

    useLayoutEffect(() => {
        let userRole = "";
        if (["sdr", "admin_sdr"].includes(user.role)) userRole = "sdr";
        else if (["csm", "admin_csm"].includes(user.role)) userRole = "csm";

        const initialFormValues = {
            company_name   : String(getSearchParams("company_name")),
            stage          : String(getSearchParams("stage")),
            max_mrr        : String(getSearchParams("max_mrr")),
            min_mrr        : String(getSearchParams("min_mrr")),
            csm            : userRole === "csm" ? { label: user.name, value: user._id } : "",
            sdr            : userRole === "sdr" ? { label: user.name, value: user._id } : "",
            product        : "",
            history_enabled: false,
            kpi            : []         
        };
        methods.reset(initialFormValues);
    }, [user]);

    useLayoutEffect(() => {
        if (!kpi?.length) return;
        const selectedKpi = kpi
            .filter(item => item.value)
            .map(item => item.name);

        methods.setValue("kpi", selectedKpi);
    }, [kpi]);

    return {
        methods,
        onSubmit,
        onReset
    };
};

export default useFilter;